import { useEffect, useMemo, useState } from 'react';
import { Box, Stack } from '@mui/material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import {
  FieldLabel,
  QueryAdderFieldGridItem,
  QueryAdderInheritedParentServiceSection,
  QueryAdderLayoutSkeleton,
  QueryAdderModalShell,
  QueryAdderScopedStyles,
  QueryAdderStickyHeader,
  QueryAdderTopicLinkingCard,
  QueryAdderUploadDropzone,
  SectionCard,
  StatusPill,
  queryAdderOneColumnFieldGridSx,
  queryAdderTwoColumnGridSx,
  renderQueryAdderCell,
} from './QueryAdderLayoutShared';
import VerificationModal from '../VerificationModal/VerificationModal';
import { ServiceOrIssueStatus, TrackerDataType } from '../../shared/constants/constants';
import { getQueryAdderUiPalette } from '../../shared/constants/queryAdderPalette';
import { LOG_ADDER_CONFIG, TOPIC_LINKING_COPY } from './logAdderConfig';

const ui = getQueryAdderUiPalette(TrackerDataType.ENGAGEMENT);
const engagementAdderConfig = LOG_ADDER_CONFIG.engagement;

interface EngagementQueryAdderLayoutProps {
  fields: Record<string, any>;
  dropdownTable: Record<string, any>;
  requiredInputs: string[];
  errorCols: string[];
  eventHandler: Record<string, any>;
  files?: any[];
  handleDrag: any;
  handleDrop: any;
  isDragActive?: boolean;
  isUpdatingRow?: boolean;
  currMonth?: string | number | Date;
  callback: (value: any, field: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  loading?: boolean;
  isUpdatingItem?: boolean;
  onDelete?: () => void;
}

interface RenderCellOptions {
  fullWidth?: boolean;
  placeholderText?: string;
  label?: string;
  info?: string;
}

const getSelectedServiceId = (selectedService: any) => {
  if (selectedService?._id) return selectedService._id;
  if (selectedService?.id) return selectedService.id;
  return '';
};

const getSelectedServiceTitle = (selectedService: any) => {
  if (selectedService?.name) return selectedService.name;
  if (selectedService?.service) return selectedService.service;
  return '';
};

function EngagementQueryAdderLayout(props: Readonly<EngagementQueryAdderLayoutProps>) {
  const {
    fields,
    dropdownTable,
    requiredInputs,
    errorCols,
    eventHandler,
    files,
    handleDrag,
    handleDrop,
    isDragActive,
    isUpdatingRow,
    currMonth,
    callback,
    onSubmit,
    onCancel,
    loading,
    isUpdatingItem,
    onDelete,
  } = props;
  const [openVerificationModal, setOpenVerificationModal] = useState(false);

  const [isLinkedToService, setIsLinkedToService] = useState(
    Array.isArray(fields.linkedService) && fields.linkedService.length > 0
  );
  const matchParentTopic = true;
  const [isTopicLinkingExpanded, setIsTopicLinkingExpanded] = useState(true);
  const [focusLinkedService, setFocusLinkedService] = useState(false);

  useEffect(() => {
    if (Array.isArray(fields.linkedService) && fields.linkedService.length > 0 && !isLinkedToService) {
      setIsLinkedToService(true);
    }
  }, [fields.linkedService, isLinkedToService]);

  const selectedService = useMemo<any>(() => {
    const linkedService = Array.isArray(fields.linkedService) ? fields.linkedService[0] : null;
    if (!linkedService) return null;
    const linkedServiceId = linkedService._id || linkedService.id || linkedService;
    return (dropdownTable?.service || []).find((service: any) => service._id === linkedServiceId) || linkedService;
  }, [dropdownTable, fields.linkedService]);

  const selectedServiceId = getSelectedServiceId(selectedService);
  const selectedServiceTitle = getSelectedServiceTitle(selectedService);
  useEffect(() => {
    if (isLinkedToService && matchParentTopic && selectedServiceTitle && fields.topic !== selectedServiceTitle) {
      callback(selectedServiceTitle, 'topic');
    }
  }, [callback, fields.topic, isLinkedToService, matchParentTopic, selectedServiceTitle]);

  const handleLinkedServiceToggle = (nextChecked: boolean) => {
    setIsLinkedToService(nextChecked);
    if (nextChecked && !selectedServiceTitle) {
      setFocusLinkedService(true);
      setIsTopicLinkingExpanded(true);
    }
    if (!nextChecked) {
      callback([], 'linkedService');
      setIsTopicLinkingExpanded(true);
    }
  };

  useEffect(() => {
    if (!focusLinkedService) return;
    const timer = setTimeout(() => setFocusLinkedService(false), 300);
    return () => clearTimeout(timer);
  }, [focusLinkedService]);

  const infoText = {
    topic:
      'Use a clear, searchable Topic name. Matching Topic names across related records keeps Topic-based Analytics cleaner.',
    type: 'Choose the Type of Engagement activity so similar work can be grouped and reported consistently.',
    expert: 'Use Lead/Expert for the spokesperson, requester, client, or subject-matter context tied to this work.',
    campaign: 'Use Campaign to group multi-part initiatives or longer-running work under one umbrella when relevant.',
    lead: 'Use Team Members for the people doing the work. This makes ownership and reporting much clearer later on.',
    communityMember:
      'Select the primary stakeholder, community member, partner, or other external participant connected to this Engagement entry.',
    organization:
      'Use Organization to capture the external institution, company, or group associated with this Engagement when relevant.',
  };
  const unitInfoText = 'Select the units connected to this Engagement.';
  const deptInfoText = 'Add the department when it helps clarify ownership or internal context for this Engagement.';
  const entryDateInfoText = 'Use Entry Date to mark when the task or work begins.';
  const dueDateInfoText = 'Use Due Date for the target timeline, or as the completion date once the work is finished.';
  const contactMethodInfoText =
    'Choose how this Engagement took place so outreach patterns are easier to review later.';
  const toneInfoText = 'Capture the tone when sentiment context is useful for follow-up or reporting.';
  const statusInfoText = 'Set the current stage of this Engagement record.';

  const fieldLabels = {
    topic: 'Topic',
    type: 'Type(s) of Work',
    expert: 'Lead/Expert',
    campaign: 'Campaign',
    lead: 'Team Members',
    communityMember: 'Stakeholder / Community Member',
    organization: 'Organization',
  };

  const renderLabeledCell = (col: string, { fullWidth = false, placeholderText }: RenderCellOptions = {}) => (
    <QueryAdderFieldGridItem key={col} fullWidth={fullWidth}>
      <FieldLabel
        label={(fieldLabels as Record<string, string>)[col] || col}
        required={requiredInputs.includes(col)}
        info={(infoText as Record<string, string>)[col]}
        ui={ui}
      />
      {renderQueryAdderCell({
        col,
        fields,
        dropdownTable,
        callback,
        currMonth,
        requiredInputs,
        errorCols,
        eventHandler,
        files,
        handleDrag,
        handleDrop,
        isDragActive,
        isUpdatingRow,
        hideLabel: true,
        labelText: (fieldLabels as Record<string, string>)[col] || col,
        fullWidthContainer: true,
        placeholderText,
      })}
    </QueryAdderFieldGridItem>
  );

  const renderGridCell = (col: string, { fullWidth = false, label, info }: RenderCellOptions = {}) => (
    <QueryAdderFieldGridItem key={col} fullWidth={fullWidth}>
      {label ? <FieldLabel label={label} required={requiredInputs.includes(col)} info={info} ui={ui} /> : null}
      {renderQueryAdderCell({
        col,
        fields,
        dropdownTable,
        callback,
        currMonth,
        requiredInputs,
        errorCols,
        eventHandler,
        files,
        handleDrag,
        handleDrop,
        isDragActive,
        isUpdatingRow,
        hideLabel: Boolean(label),
        labelText: label,
        fullWidthContainer: true,
      })}
    </QueryAdderFieldGridItem>
  );

  const inheritedFields = (
    <QueryAdderInheritedParentServiceSection
      ui={ui}
      infoText="These fields inherit from the selected parent Service so the linked work stays connected in one workflow."
    >
      {renderGridCell('units', { label: 'Unit', info: unitInfoText })}
      {renderGridCell('dept', { label: 'Department', info: deptInfoText })}
      {renderLabeledCell('expert')}
      {renderLabeledCell('campaign')}
    </QueryAdderInheritedParentServiceSection>
  );

  const topicLinkingSearchField = (
    <Box sx={{ width: '100%' }}>
      {renderQueryAdderCell({
        col: 'linkedService',
        fields,
        dropdownTable,
        callback,
        currMonth,
        requiredInputs,
        errorCols,
        eventHandler,
        files,
        handleDrag,
        handleDrop,
        isDragActive,
        isUpdatingRow,
        hideLabel: true,
        labelText: 'Search Services',
        placeholderText: 'Search Services',
        autoFocusLinkedService: focusLinkedService,
        fullWidthContainer: true,
      })}
    </Box>
  );

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 0,
        color: ui.text,
        backgroundColor: ui.white,
      }}
    >
      <QueryAdderScopedStyles
        scopeClassName=".engagement-query-adder"
        ui={ui}
        options={{ preserveFieldChrome: true, preserveRichTextChrome: true }}
      />
      <Box className="engagement-query-adder" sx={{ height: '100%', minHeight: 0, backgroundColor: ui.white }}>
        <QueryAdderModalShell
          header={
            <QueryAdderStickyHeader
              title={engagementAdderConfig.modalTitle}
              infoText="Capture stakeholder, community, partner, or government activity and keep it connected to the broader Service when relevant."
              icon={engagementAdderConfig.icon}
              ui={ui}
              onClose={onCancel}
              onSubmit={onSubmit}
              onCancel={onCancel}
              onDelete={onDelete ? () => setOpenVerificationModal(true) : undefined}
              isUpdatingItem={isUpdatingItem || loading}
              isUpdatingRow={isUpdatingRow}
              topBorderColor={engagementAdderConfig.logColor}
              titleSx={{ color: ui.title }}
              iconCardSx={{
                backgroundColor: ui.tile,
                color: ui.accentDark,
                borderRadius: '12px',
              }}
            />
          }
          bodySx={{ backgroundColor: ui.white }}
          contentSx={{ pb: 4, backgroundColor: ui.white }}
        >
          {loading ? (
            <QueryAdderLayoutSkeleton ui={ui} />
          ) : (
            <Box component="form" onSubmit={onSubmit} sx={{ backgroundColor: ui.white }}>
              <Stack spacing={3}>
                <QueryAdderTopicLinkingCard
                  ui={ui}
                  eyebrow={TOPIC_LINKING_COPY.sectionTitle}
                  checkboxLabel={TOPIC_LINKING_COPY.panelTitle}
                  checkboxHelperText={TOPIC_LINKING_COPY.helperText}
                  checked={isLinkedToService}
                  onCheckedChange={handleLinkedServiceToggle}
                  selectedServiceTitle={selectedServiceTitle}
                  selectedServiceId={selectedServiceId}
                  expanded={isTopicLinkingExpanded}
                  onExpandedChange={setIsTopicLinkingExpanded}
                  searchField={topicLinkingSearchField}
                  inheritedFields={selectedService ? inheritedFields : null}
                  inheritedInfoText="Use this when this record belongs under an existing Service."
                />

                <SectionCard title="Details" icon={<GroupsOutlinedIcon sx={{ fontSize: 20 }} />} ui={ui}>
                  <Box sx={queryAdderTwoColumnGridSx}>
                    {isLinkedToService ? null : (
                      <>
                        {renderLabeledCell('topic', { placeholderText: 'Select or create a Topic', fullWidth: true })}
                        {renderGridCell('units', { label: 'Unit', info: unitInfoText })}
                        {renderGridCell('dept', { label: 'Department', info: deptInfoText })}
                        {renderLabeledCell('expert')}
                        {renderLabeledCell('campaign')}
                      </>
                    )}
                    {renderLabeledCell('type')}
                    {renderLabeledCell('lead')}
                    {renderGridCell('entryDate', { label: 'Entry Date', info: entryDateInfoText })}
                    {renderGridCell('dueDate', { label: 'Due Date', info: dueDateInfoText })}
                    {renderGridCell('contactMethod', { label: 'Mode of Communication', info: contactMethodInfoText })}
                    {renderLabeledCell('communityMember')}
                    {renderLabeledCell('organization')}
                    {renderGridCell('tone', { label: 'Tone', info: toneInfoText })}
                    <QueryAdderFieldGridItem>
                      <FieldLabel
                        label="Status"
                        required={requiredInputs.includes('status')}
                        info={statusInfoText}
                        ui={ui}
                      />
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {[
                          ServiceOrIssueStatus.IN_PROGRESS,
                          ServiceOrIssueStatus.COMPLETED,
                          ServiceOrIssueStatus.PARTICIPATION_RIBBON,
                          ServiceOrIssueStatus.CANCELLED,
                        ].map((statusOption) => (
                          <StatusPill
                            key={statusOption}
                            label={statusOption as any}
                            active={fields.status === statusOption}
                            onClick={() => callback(statusOption, 'status')}
                            ui={ui}
                          />
                        ))}
                      </Stack>
                    </QueryAdderFieldGridItem>
                  </Box>
                </SectionCard>

                <SectionCard title="Notes and Files" icon={<FolderOpenOutlinedIcon sx={{ fontSize: 20 }} />} ui={ui}>
                  <Box sx={queryAdderOneColumnFieldGridSx}>
                    {renderGridCell('background', { label: 'Background Information', fullWidth: true })}
                    {renderGridCell('notes', { label: 'Notes/Details', fullWidth: true })}
                  </Box>

                  <Box sx={{ mt: 2.5 }}>
                    <FieldLabel
                      label="Files and Links"
                      info="Attach briefing notes, meeting materials, PDFs, or supporting links for this Engagement."
                      ui={ui}
                    />
                    <QueryAdderUploadDropzone
                      ui={ui}
                      description="Attach briefing notes, meeting materials, PDFs, or supporting links for this Engagement."
                      handleDrag={handleDrag}
                      handleDrop={handleDrop}
                      isDragActive={isDragActive}
                    >
                      {renderQueryAdderCell({
                        col: 'file',
                        fields,
                        dropdownTable,
                        callback,
                        currMonth,
                        requiredInputs,
                        errorCols,
                        eventHandler,
                        files,
                        handleDrag,
                        handleDrop,
                        isDragActive,
                        isUpdatingRow,
                        hideLabel: true,
                        fullWidthContainer: true,
                        fileRenderMode: 'uploadOnly',
                      })}
                    </QueryAdderUploadDropzone>
                    {files && files.length > 0 ? (
                      <Box sx={{ mt: 2 }}>
                        {renderQueryAdderCell({
                          col: 'file',
                          fields,
                          dropdownTable,
                          callback,
                          currMonth,
                          requiredInputs,
                          errorCols,
                          eventHandler,
                          files,
                          handleDrag,
                          handleDrop,
                          isDragActive,
                          isUpdatingRow,
                          hideLabel: true,
                          fullWidthContainer: true,
                          fileRenderMode: 'listOnly',
                        })}
                      </Box>
                    ) : null}
                  </Box>
                </SectionCard>
              </Stack>
            </Box>
          )}
        </QueryAdderModalShell>
      </Box>
      {onDelete ? (
        <VerificationModal
          openVerificationModal={openVerificationModal}
          setOpenVerificationModal={setOpenVerificationModal}
          modalTitle="Confirm Deletion"
          modalDescription="Are you sure you want to delete this Engagement?"
          accentColor={ui.accentDark || ui.main}
          handleYesClick={() => {
            onDelete();
            setOpenVerificationModal(false);
          }}
        />
      ) : null}
    </Box>
  );
}

export default EngagementQueryAdderLayout;
